// Cart upgrades — insured shipping + gift wrap checkboxes.
// Loaded globally from theme.liquid so the customElement is defined before
// any cart-drawer morph inserts a <cart-upgrades> element (the snippet's
// inline <script> doesn't execute when injected via innerHTML morph).

(() => {
  if (customElements.get('cart-upgrades')) return;

  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const variantCache = new Map();

  async function fetchProductJson(key) {
    try {
      const res = await fetch(`/products/${key}.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  async function resolveVariantId(input) {
    const fromAttr = input.dataset.variantId;
    if (fromAttr && fromAttr !== '') return fromAttr;

    const handle = input.dataset.handle;
    const productId = input.dataset.productId;
    const cacheKey = handle || productId;
    if (cacheKey && variantCache.has(cacheKey)) return variantCache.get(cacheKey);

    let product = null;
    if (handle) product = await fetchProductJson(handle);
    if (!product && productId) product = await fetchProductJson(productId);
    if (!product) return null;

    const firstAvailable = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
    const id = firstAvailable ? String(firstAvailable.id) : null;
    if (id) {
      if (cacheKey) variantCache.set(cacheKey, id);
      input.dataset.variantId = id;
    }
    return id;
  }

  class CartUpgrades extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this._busy = false;
      this._onChange = this._onChange.bind(this);
      this.addEventListener('change', this._onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
    }

    async _onChange(event) {
      const input = event.target.closest('.cart-upgrades__input');
      if (!input || this._busy) return;

      const wasChecked = input.checked;
      const handle = input.dataset.handle;
      this._busy = true;
      this._setDisabled(true);

      try {
        const variantId = await resolveVariantId(input);
        if (!variantId) {
          throw new Error(
            `Upgrade-product "${handle}" niet gevonden. ` +
            `Controleer in Shopify Admin: Status = Active en gepubliceerd op Online Store.`
          );
        }

        // Re-derive desired action from the actual cart, not from the
        // checkbox visual state. The drawer's morph can leave a stale
        // checked state (e.g. after the upsell-popup added the same
        // product), which would otherwise turn an add-click into a
        // remove-click and vice versa.
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await cartRes.json();
        const alreadyInCart = (cart.items || []).some((it) =>
          (variantId && String(it.variant_id) === String(variantId)) ||
          (handle && it.handle === handle)
        );

        if (wasChecked && !alreadyInCart) {
          await this._addItem(variantId);
        } else if (!wasChecked && alreadyInCart) {
          await this._removeItem(variantId, handle);
        } else {
          await this._refreshSections();
        }
      } catch (err) {
        input.checked = !wasChecked;
        console.error('[cart-upgrades]', err);
      } finally {
        this._busy = false;
        this._setDisabled(false);
      }
    }

    _setDisabled(state) {
      this.querySelectorAll('.cart-upgrades__input').forEach((el) => {
        el.disabled = state;
      });
    }

    _sectionsToUpdate() {
      const sections = new Set();
      if (this.sectionId) sections.add(this.sectionId);
      document
        .querySelectorAll(
          'cart-items-component[data-section-id], cart-icon[data-section-id], cart-drawer-component[data-section-id]'
        )
        .forEach((el) => {
          const id = el.dataset.sectionId;
          if (id) sections.add(id);
        });
      return Array.from(sections);
    }

    async _addItem(variantId) {
      const sections = this._sectionsToUpdate();
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || 'Kon upgrade niet toevoegen');
      this._notifyCartUpdate(data);
    }

    async _removeItem(variantId, handle) {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      let lineIndex = cart.items.findIndex(
        (item) => String(item.variant_id) === String(variantId)
      );
      if (lineIndex === -1 && handle) {
        lineIndex = cart.items.findIndex((item) => item.handle === handle);
      }
      if (lineIndex === -1) {
        await this._refreshSections();
        return;
      }

      const sections = this._sectionsToUpdate();
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({
          line: lineIndex + 1,
          quantity: 0,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error('Kon upgrade niet verwijderen');
      this._notifyCartUpdate(data);
    }

    async _refreshSections() {
      const sections = this._sectionsToUpdate();
      const url = `/cart.js?sections=${encodeURIComponent(sections.join(','))}&sections_url=${encodeURIComponent(window.location.pathname)}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      const data = await res.json();
      this._notifyCartUpdate(data);
    }

    _notifyCartUpdate(data) {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: 'cart-upgrades',
            data: {
              source: 'cart-upgrades',
              itemCount: data.item_count,
              sections: data.sections,
            },
          },
        })
      );
    }
  }

  customElements.define('cart-upgrades', CartUpgrades);
})();
